<template>
  <div class="speech-schedule-container">
    <!-- Speech Input Section -->
    <div class="speech-input-section">
      <div class="input-group">
       <button
          @click="toggleRecording"
          class="mic-button"
          :class="{ recording: isListening }"
          :disabled="!isSupported"
        >
          <span v-if="!isSupported">❌</span>
          <span v-else-if="isListening">⏹ Stop</span>
          <span v-else>🎤 Speak</span>
        </button>
        
        <!--input
          v-model="spokenQuery"
          type="text"
          placeholder='e.g., "show schedule between 1 January and 7 January"'
          class="query-input"
          @keyup.enter="processSpeechQuery"
        /-->

        
        <input
          v-model="spokenQuery"
          type="text"
          placeholder='e.g., "Between 1 January and 7 January"'
          class="query-input"
          @keyup.enter="processSpeechQuery"
        />
      </div>

      <!-- Error messages -->
      <div v-if="errorMessage" class="error-message">
        ⚠️ {{ errorMessage }}
      </div>
    </div>

    <!-- Schedule Results -->
    <div v-if="isLoading" class="loading">
      <div class="spinner"></div>
      Loading schedule...
    </div>

    <div v-if="scheduleData && !isLoading" class="schedule-results">
      <!--div class="results-header">
        <h3>📊 Schedule Results</h3>
        <span class="total-hours">Total: {{ grandTotalHours }}</span>
      </div -->

      <!-- Your existing schedule grid here -->
      <div class="schedule-grid">
        <slot name="schedule-display" :schedule="scheduleData" :dates="dateList" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useSpeechRecognition } from '@vueuse/core'
import { format, differenceInDays, isValid, isAfter } from 'date-fns'

const props = defineProps<{
  baseUrl: string
}>()

const emit = defineEmits<{
  (e: 'schedule-loaded', data: any): void
}>()

// ============ STATE ============
const spokenQuery = ref('')
const parsedStartDate = ref<Date | null>(null)
const parsedEndDate = ref<Date | null>(null)
const errorMessage = ref('')
const isLoading = ref(false)
const scheduleData = ref<any>(null)
const dateList = ref<any[]>([])

// Speech recognition
const {
  isSupported,
  isListening,
  result,
  interimResults,
  start,
  stop,
} = useSpeechRecognition({
  lang: 'en-US',
  continuous: false,
  interimResults: true,
})

// Watch for final speech result
watch(result, (newText) => {
  if (newText) {
    spokenQuery.value = newText
    processSpeechQuery()
  }
})

// Watch for interim results
const interimText = ref('')
watch(interimResults, (newText) => {
  interimText.value = newText || ''
})

// ============ DATE PARSING ============
const parseDateFromText = (text: string): Date | null => {
  // Common date patterns
  const patterns = [
    /(\d{1,2})(?:st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December)(?:\s+(\d{4}))?/i,
    /(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2})(?:st|nd|rd|th)?/i,
    /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/,
    /(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/,
    /(today|tomorrow)/i,
  ]

  const currentYear = new Date().getFullYear()

  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (match) {
      if (match[0].toLowerCase() === 'today') {
        return getDateWithoutTime(new Date())
      }
      if (match[0].toLowerCase() === 'tomorrow') {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        return getDateWithoutTime(tomorrow)
      }

      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December']
      
      let day, month, year

      if (match[1] && monthNames.some(m => m.toLowerCase() === match[2]?.toLowerCase())) {
        day = parseInt(match[1])
        const monthIndex = monthNames.findIndex(m => m.toLowerCase() === match[2].toLowerCase())
        month = monthIndex + 1
        year = match[3] ? parseInt(match[3]) : currentYear
      }
      else if (match[1] && monthNames.some(m => m.toLowerCase() === match[1].toLowerCase())) {
        const monthIndex = monthNames.findIndex(m => m.toLowerCase() === match[1].toLowerCase())
        month = monthIndex + 1
        day = parseInt(match[2])
        year = currentYear
      }
      else if (match[1] && match[2] && match[3]) {
        const first = parseInt(match[1])
        const second = parseInt(match[2])
        const yearVal = parseInt(match[3])
        
        if (first > 12 && second <= 12) {
          day = first
          month = second
        } else if (first <= 12 && second > 12) {
          day = second
          month = first
        } else {
          day = first
          month = second
        }
        year = yearVal
      }
      else {
        continue
      }

      // Create date WITHOUT timezone offset
      const date = createDateWithoutTimezone(year, month - 1, day)
      if (isValid(date)) {
        return date
      }
    }
  }

  return null
}

// Helper function to create date without timezone offset
const createDateWithoutTimezone = (year: number, month: number, day: number): Date => {
  return new Date(Date.UTC(year, month, day))
}

// Helper function to get date without time
const getDateWithoutTime = (date: Date): Date => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
}

const extractDatesFromQuery = (query: string): { start: Date | null; end: Date | null } => {
  // Common date markers
  const patterns = [
    /between\s+([^.]+?)\s+and\s+([^.]+?)(?:\s|$)/i,
    /from\s+([^.]+?)\s+to\s+([^.]+?)(?:\s|$)/i,
    /(\d{1,2}(?:st|nd|rd|th)?\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)(?:\s+\d{4})?)\s+(?:to|and|through)\s+(\d{1,2}(?:st|nd|rd|th)?\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)(?:\s+\d{4})?)/i,
  ]

  for (const pattern of patterns) {
    const match = query.match(pattern)
    if (match) {
      const startDate = parseDateFromText(match[1])
      const endDate = parseDateFromText(match[2])
      
      if (startDate && endDate) {
        return { start: startDate, end: endDate }
      }
    }
  }

  // Try to find two dates anywhere in the text
  const allDates = query.match(/\d{1,2}(?:st|nd|rd|th)?\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)(?:\s+\d{4})?/gi)
  if (allDates && allDates.length >= 2) {
    const start = parseDateFromText(allDates[0])
    const end = parseDateFromText(allDates[allDates.length - 1])
    if (start && end) {
      return { start, end }
    }
  }

  // Try single date (use today as start)
  const singleDate = parseDateFromText(query)
  if (singleDate) {
    return { start: singleDate, end: singleDate }
  }

  return { start: null, end: null }
}

// ============ QUERY PROCESSING ============
const processSpeechQuery = async () => {
  if (!spokenQuery.value.trim()) {
    errorMessage.value = 'Please speak or type a query.'
    return
  }

  errorMessage.value = ''
  isLoading.value = true

  try {
    const { start, end } = extractDatesFromQuery(spokenQuery.value)
    
    if (!start || !end) {
      errorMessage.value = 'Could not find dates in your query.'
      isLoading.value = false
      return
    }

    if (isAfter(start, end)) {
      errorMessage.value = 'Start date cannot be after end date.'
      isLoading.value = false
      return
    }

    parsedStartDate.value = start
    parsedEndDate.value = end

    // Call the schedule API
    await fetchSchedule(start, end)

  } catch (error) {
    console.error('Error processing query:', error)
    errorMessage.value = 'Failed to process your request. Please try again.'
  } finally {
    isLoading.value = false
  }
}

// ============ API CALL ============
const fetchSchedule = async (startDate: Date, endDate: Date) => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      errorMessage.value = 'Please login first.'
      return
    }

    const response = await fetch(props.baseUrl + 'schedule/ScheduleDictionaryForDateRangePageWise', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        StartDate: startDate,
        EndDate: endDate,
        PageNumber: 0,
        RecordsPerPage: 5000,
        SearchString: '',
        CompanyId: 0
      })
    })

    if (response.status === 401) {
      errorMessage.value = 'Session expired. Please login again.'
      return
    }

    const data = await response.json()
    const scheduleDataResult = data.scheduleSinglePageResult || {}

    // Process dates for display
    const dateKeys = Object.keys(scheduleDataResult).sort()
    const dates = dateKeys.map(key => ({
      dateKey: key,
      date: new Date(key)
    }))

    scheduleData.value = scheduleDataResult
    dateList.value = dates

    // ✅ FIX: Emit BOTH start and end dates to parent
    emit('schedule-loaded', { 
      schedule: scheduleDataResult, 
      dates,
      startDate: startDate,
      endDate: endDate
    })

  } catch (error) {
    console.error('Error fetching schedule:', error)
    throw error
  }
}

// ============ TOGGLE RECORDING ============
const toggleRecording = () => {
  if (isListening.value) {
    stop()
  } else {
    spokenQuery.value = ''
    parsedStartDate.value = null
    parsedEndDate.value = null
    errorMessage.value = ''
    start()
  }
}

// ============ HELPERS ============
const formatDate = (date: Date): string => {
  return format(date, 'dd MMM yyyy')
}

const daysBetween = (start: Date, end: Date): number => {
  return differenceInDays(end, start) + 1
}

const grandTotalHours = computed(() => {
  if (!scheduleData.value) return '0h'
  // Calculate total hours from schedule data
  // Your existing calculation here
  return '0h'
})

// ============ EXAMPLE QUERIES ============
const examples = [
  'show schedule between 1st January and 15th January',
  'show schedule from today to next week',
  'show schedule between 5 March and 20 March',
  'show schedule for 15 February'
]
</script>
<style scoped>
.speech-schedule-container {
  max-width: 100%;
  padding: 20px;
}

/* Speech Input Section */
.speech-input-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.input-group {
  display: flex;
  gap: 10px;
  align-items: center;
  /* CRITICAL FIX: Prevent wrapping on mobile */
  flex-wrap: nowrap;
  width: 100%;
}

.query-input {
  flex: 1;
  /* CRITICAL FIX: Allow input to shrink on mobile */
  min-width: 0;
  padding: 12px 16px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s;
}

.query-input:focus {
  border-color: #4a90d9;
}

.mic-button {
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  background: #4a90d9;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.3s;
  /* Prevent button from shrinking */
  flex-shrink: 0;
  white-space: nowrap;
}

.mic-button:hover:not(:disabled) {
  background: #357abd;
}

.mic-button.recording {
  background: #e74c3c;
  animation: pulse 1.5s infinite;
}

.mic-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.search-button {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: #27ae60;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.3s;
  /* Prevent button from shrinking */
  flex-shrink: 0;
  white-space: nowrap;
}

.search-button:hover:not(:disabled) {
  background: #229954;
}

.search-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Interim Text */
.interim-text {
  margin-top: 12px;
  padding: 10px 14px;
  background: white;
  border-radius: 6px;
  border-left: 4px solid #e74c3c;
  font-size: 14px;
  color: #555;
}

.listening-indicator {
  color: #e74c3c;
  font-weight: bold;
  margin-right: 8px;
}

/* Parsed Dates Display */
.parsed-dates {
  margin-top: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.date-badge {
  padding: 6px 14px;
  background: #e8f4fd;
  border-radius: 20px;
  font-size: 14px;
  color: #1a5276;
  border: 1px solid #aed6f1;
}

.days-count {
  font-size: 14px;
  color: #555;
  font-weight: 500;
}

/* Error Message */
.error-message {
  margin-top: 12px;
  padding: 10px 14px;
  background: #fde8e8;
  border-radius: 6px;
  border-left: 4px solid #e74c3c;
  color: #c0392b;
}

/* Loading */
.loading {
  text-align: center;
  padding: 40px;
}

.spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Results */
.schedule-results {
  margin-top: 20px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  margin-bottom: 16px;
}

.results-header h3 {
  margin: 0;
}

.total-hours {
  background: rgba(255,255,255,0.2);
  padding: 4px 14px;
  border-radius: 20px;
  font-weight: 600;
}

/* Example Queries */
.example-queries {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.example-label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.example-btn {
  padding: 4px 12px;
  border: 1px solid #ddd;
  border-radius: 16px;
  background: white;
  color: #555;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.example-btn:hover {
  background: #4a90d9;
  color: white;
  border-color: #4a90d9;
}

/* Schedule Grid (copy your existing styles) */
.schedule-grid {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

/* ============ MOBILE FIX - ONLY LAYOUT CHANGES ============ */
/* These changes only affect layout, not functionality */
@media (max-width: 768px) {
  .speech-schedule-container {
    padding: 10px;
  }
  
  .speech-input-section {
    padding: 12px 15px;
  }
  
  .input-group {
    gap: 8px;
  }
  
  .query-input {
    padding: 10px 12px;
    font-size: 14px;
    min-width: 0; /* KEY FIX: Allows input to shrink */
  }
  
  .mic-button {
    padding: 10px 14px;
    font-size: 14px;
    flex-shrink: 0; /* KEY FIX: Prevents button from shrinking */
  }
  
  .search-button {
    padding: 10px 16px;
    font-size: 14px;
    flex-shrink: 0; /* KEY FIX: Prevents button from shrinking */
  }
}

/* For very small screens - even more compact but still one row */
@media (max-width: 480px) {
  .speech-schedule-container {
    padding: 8px;
  }
  
  .speech-input-section {
    padding: 10px 12px;
  }
  
  .input-group {
    gap: 6px;
  }
  
  .query-input {
    padding: 8px 10px;
    font-size: 13px;
    min-width: 0;
  }
  
  .query-input::placeholder {
    font-size: 12px;
  }
  
  .mic-button {
    padding: 8px 12px;
    font-size: 13px;
    flex-shrink: 0;
  }
  
  .search-button {
    padding: 8px 12px;
    font-size: 13px;
    flex-shrink: 0;
  }
}
</style>